import { useState, useEffect } from 'react';
import { Card, Stack, Group, Text, Button } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { TextInput, Select } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { ApiError } from '@repletoryreach/sdk-api';
import { usePayoutAccount, useSavePayoutAccount } from '../../../data/useAffiliate';
import { useT } from '../../../i18n/I18nProvider';

/**
 * Where a bank payment goes.
 *
 * The account is shown masked, because a page left open on a laptop should
 * not read out somebody's account number, and it cannot be changed while a
 * withdrawal is waiting: moving the destination of money already on its way is
 * exactly what an account takeover would do. Every change is emailed.
 */
export function PayoutAccountCard({ locked }: { locked: boolean }) {
  const t = useT();
  const account = usePayoutAccount();
  const save = useSavePayoutAccount();
  const [open, setOpen] = useState(false);
  const [holder, setHolder] = useState('');
  const [bank, setBank] = useState('');
  const [branch, setBranch] = useState('');
  const [number, setNumber] = useState('');
  const [type, setType] = useState('cheque');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setHolder(account.data?.account_holder ?? '');
    setBank(account.data?.bank_name ?? '');
    setBranch(account.data?.branch_code ?? '');
    setNumber('');
    setType(account.data?.account_type ?? 'cheque');
  }, [open, account.data]);

  async function submit() {
    setError(null);
    try {
      await save.mutateAsync({
        account_holder: holder.trim(),
        bank_name: bank.trim(),
        branch_code: branch.trim(),
        account_number: number.trim(),
        account_type: type
      });
      notifySuccess(t('refer.payout.saved'));
      setOpen(false);
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: { message?: string } } | null) : null;
      setError(body?.error?.message ?? t('refer.payout.failed'));
    }
  }

  return (
    <>
      <Card withBorder>
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <Stack gap="xs">
              <Text role="label">{t('refer.payout.title')}</Text>
              <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('refer.payout.description')}</Text>
            </Stack>
            <Button variant="light" size="sm" onClick={() => setOpen(true)} disabled={locked}>
              {account.data ? t('refer.payout.change') : t('refer.payout.add')}
            </Button>
          </Group>

          {locked && <Alert color="warning">{t('refer.payout.locked')}</Alert>}

          {account.isLoading ? (
            <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('common.loading')}</Text>
          ) : account.isError ? (
            <Alert color="error" role="alert">
              {t('refer.payout.loadFailed')}{' '}
              <Button variant="subtle" size="sm" onClick={() => void account.refetch()}>{t('action.retry')}</Button>
            </Alert>
          ) : account.data ? (
            <Stack gap="xs">
              <Text>{account.data.account_holder}</Text>
              <Text role="bodySmall" c="var(--rr-color-text-muted)">
                {account.data.bank_name} · {account.data.branch_code} · {account.data.account_number_masked}
              </Text>
            </Stack>
          ) : (
            <EmptyState title={t('refer.payout.emptyTitle')} description={t('refer.payout.emptyBody')} />
          )}
        </Stack>
      </Card>

      <Drawer opened={open} onClose={() => setOpen(false)} position="right" size="md" title={t('refer.payout.title')}>
        <Stack gap="md">
          {error && <Alert color="error" role="alert">{error}</Alert>}
          <TextInput label={t('refer.payout.holder')} description={t('refer.payout.holderHint')} value={holder} onChange={(e) => setHolder(e.currentTarget.value)} required />
          <TextInput label={t('refer.payout.bank')} value={bank} onChange={(e) => setBank(e.currentTarget.value)} required />
          <TextInput label={t('refer.payout.branch')} description={t('refer.payout.branchHint')} value={branch} onChange={(e) => setBranch(e.currentTarget.value)} required inputMode="numeric" />
          <TextInput label={t('refer.payout.number')} description={t('refer.payout.numberHint')} value={number} onChange={(e) => setNumber(e.currentTarget.value)} required inputMode="numeric" />
          <Select
            label={t('refer.payout.type')}
            value={type}
            onChange={(e) => setType(e.currentTarget.value)}
            data={[
              { value: 'cheque', label: t('refer.payout.typeCheque') },
              { value: 'savings', label: t('refer.payout.typeSavings') },
              { value: 'transmission', label: t('refer.payout.typeTransmission') }
            ]}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setOpen(false)}>{t('action.cancel')}</Button>
            <Button onClick={submit} loading={save.isPending} disabled={!holder.trim() || !bank.trim() || !branch.trim() || !number.trim()}>
              {t('action.save')}
            </Button>
          </Group>
        </Stack>
      </Drawer>
    </>
  );
}
